import { useEffect, useMemo } from "react";

import { useStatsStore } from "../../state/useStatsStore";
import { useCrossSection } from "../../lib/useStats";
import {
  LEVEL_LABELS,
  LEVEL_NOUN,
  LEVEL_PLURAL,
  MEASURE_BY_ID,
  strength,
  type Level,
} from "../../lib/stats";
import { num } from "../../lib/format";
import { Figure, SectionTitle, Select } from "../ui/Field";
import { StatsScatter } from "./StatsScatter";

const LEVELS: Level[] = ["departement", "commune", "iris"];

const DEPARTMENTS = [
  { value: "all", label: "All of Île-de-France" },
  { value: "75", label: "Paris" },
  { value: "77", label: "Seine-et-Marne" },
  { value: "78", label: "Yvelines" },
  { value: "91", label: "Essonne" },
  { value: "92", label: "Hauts-de-Seine" },
  { value: "93", label: "Seine-Saint-Denis" },
  { value: "94", label: "Val-de-Marne" },
  { value: "95", label: "Val-d'Oise" },
];

const MIN_SALES = [0, 10, 25, 50];

/**
 * Any measure against any other, across the whole region.
 *
 * The rest of the app answers "what is true here". This answers "do these move
 * together", which is a question about the region rather than about a flat —
 * and the difference is the thing most easily lost. Three guards keep it
 * visible: the sample size sits next to every correlation, the same coefficient
 * is computed at all three geographies so a relationship that depends on the
 * grouping cannot hide, and the footer says plainly that none of this is about
 * any individual property.
 */
export function StatsPanel({ visible }: { visible: boolean }) {
  const s = useStatsStore();

  const query = useMemo(
    () => ({
      level: s.level, x: s.x, y: s.y,
      logX: s.logX, logY: s.logY,
      dep: s.dep, minSales: s.minSales,
    }),
    [s.level, s.x, s.y, s.logX, s.logY, s.dep, s.minSales],
  );

  const {
    isLoading, isError, measures, points, fit, agreement,
    logX, logY, logXRefused, logYRefused,
  } = useCrossSection(query);

  // Changing geography can take a measure away — the census has no consistent
  // neighbourhood series — so an axis that no longer exists falls back rather
  // than leaving the pane plotting nothing with no explanation.
  const ids = useMemo(() => new Set(measures.map((m) => m.id)), [measures]);
  useEffect(() => {
    if (measures.length === 0) return;
    if (!ids.has(s.x)) s.setX(measures[0].id);
    if (!ids.has(s.y)) s.setY(measures[Math.min(1, measures.length - 1)].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, s.x, s.y, measures]);

  const xMeasure = MEASURE_BY_ID[s.x];
  const yMeasure = MEASURE_BY_ID[s.y];
  const ready = xMeasure && yMeasure && ids.has(s.x) && ids.has(s.y);

  const options = measures.map((m) => ({
    value: m.id,
    label: `${m.group} · ${m.label}`,
  }));

  return (
    <div className="px-4 py-3">
      <section>
        <div className="grid grid-cols-2 gap-2">
          <Select
            label="Compare across"
            value={s.level}
            options={LEVELS.map((l) => ({ value: l, label: LEVEL_LABELS[l] }))}
            onChange={s.setLevel}
            hint="Aggregating the same places into bigger zones can change a correlation, and sometimes its sign."
          />
          <Select
            label="Within"
            value={s.dep}
            options={DEPARTMENTS}
            onChange={s.setDep}
          />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Select label="Horizontal" value={s.x} options={options} onChange={s.setX} />
          <Select label="Vertical" value={s.y} options={options} onChange={s.setY} />
        </div>
        <div className="mt-2 flex items-center gap-4">
          <LogToggle
            id="stats-log-x"
            label="log x"
            on={s.logX}
            refused={logXRefused}
            set={s.setLogX}
          />
          <LogToggle
            id="stats-log-y"
            label="log y"
            on={s.logY}
            refused={logYRefused}
            set={s.setLogY}
          />
          <div className="ml-auto w-28">
            <Select
              label="Min sales"
              value={s.minSales}
              options={MIN_SALES.map((n) => ({ value: n, label: n === 0 ? "any" : `${n}+` }))}
              onChange={s.setMinSales}
              hint="Drops zones whose price rests on very few recorded sales."
            />
          </div>
        </div>
      </section>

      <div className="mt-4 border-t border-line pt-3">
        {isLoading ? (
          <div className="h-56 animate-pulse rounded-lg bg-surface-alt" />
        ) : isError ? (
          <p className="rounded-lg bg-surface-alt px-3 py-3 text-center text-[12px] leading-snug text-ink-soft">
            Cross-section not built — run <code>make stats</code>.
          </p>
        ) : !ready ? (
          <p className="rounded-lg bg-surface-alt px-3 py-3 text-center text-[12px] leading-snug text-ink-soft">
            Pick two measures to compare.
          </p>
        ) : fit.n < 3 ? (
          <p className="rounded-lg bg-surface-alt px-3 py-3 text-center text-[12px] leading-snug text-ink-soft">
            Only {fit.n} {fit.n === 1 ? LEVEL_NOUN[s.level] : LEVEL_PLURAL[s.level]}{" "}
            have both measures — too few to fit anything.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <Figure
                label="Correlation"
                value={fit.r === null ? "—" : fit.r.toFixed(2)}
                sub={strength(fit.r)}
                accent={fit.r !== null && Math.abs(fit.r) >= 0.5}
                hint="Pearson's r, on the plotted scale. −1 is a perfect inverse, +1 a perfect match, 0 nothing."
              />
              <Figure
                label="Rank corr."
                value={fit.rho === null ? "—" : fit.rho.toFixed(2)}
                sub="Spearman ρ"
                hint="Correlation of ranks rather than values. Trust this one when either measure is skewed or has outliers."
              />
              <Figure
                label="Explains"
                value={fit.r2 === null ? "—" : `${Math.round(fit.r2 * 100)}%`}
                sub={`of ${num(fit.n)} ${LEVEL_PLURAL[s.level]}`}
                hint="R² — the share of the variation in the vertical measure that the straight line accounts for."
              />
            </div>

            {visible && (
              <div className="mt-3">
                <StatsScatter
                  points={points}
                  xMeasure={xMeasure}
                  yMeasure={yMeasure}
                  logX={logX}
                  logY={logY}
                  fit={fit}
                />
              </div>
            )}

            <div className="mt-4 border-t border-line pt-3">
              <SectionTitle>Does it survive regrouping?</SectionTitle>
              <ul className="space-y-1">
                {agreement.byLevel.map((row) => (
                  <li key={row.level} className="flex items-baseline gap-2">
                    <span
                      className={`min-w-0 flex-1 truncate text-[11px] ${
                        row.level === s.level ? "font-semibold" : "text-ink-soft"
                      }`}
                    >
                      {LEVEL_LABELS[row.level]}
                    </span>
                    <span className="shrink-0 text-[11px] font-semibold tabular-nums">
                      {row.r === null ? "—" : row.r.toFixed(2)}
                    </span>
                    <span className="w-24 shrink-0 text-right text-[10px] tabular-nums text-ink-soft">
                      {num(row.n)} zones
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-[10px] leading-snug text-ink-soft">
                {agreement.signFlips
                  ? "These disagree on direction. A correlation between area averages describes areas, not the flats inside them — regroup the same places differently and the answer can invert. Neither number is wrong; they answer different questions."
                  : agreement.strengthVaries
                    ? "Same direction everywhere, different strength. Bigger zones average away the variation inside them, which usually tightens a correlation without adding evidence."
                    : "The same direction and roughly the same strength at every grouping, which is the most this can offer as a sign the relationship is real."}
              </p>
            </div>

            <p className="mt-4 border-t border-line pt-2 text-[10px] leading-snug text-ink-soft">
              This is a shape across {LEVEL_PLURAL[s.level]}, not a claim about
              any one of them, and never a cause: heat and price both rise with
              density, which is doing most of the work in that particular pair.
              Zones missing either measure are dropped, so the sample changes
              with the axes — that is what the count beside R² is for.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * `refused` means the box is ticked but the axis carries zero or negative
 * values, so the scale stayed linear. Said out loud rather than left to be
 * inferred from a point count that quietly shrank.
 */
function LogToggle({
  id, label, on, refused, set,
}: {
  id: string;
  label: string;
  on: boolean;
  refused: boolean;
  set: (v: boolean) => void;
}) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-center gap-1.5"
      title={
        refused
          ? "This measure goes to zero or below, which a log scale cannot show — kept linear rather than dropping those zones."
          : undefined
      }
    >
      <input
        id={id}
        type="checkbox"
        checked={on}
        onChange={(e) => set(e.target.checked)}
        className="h-3.5 w-3.5 accent-accent"
      />
      <span className={`text-[11px] ${refused ? "text-ink-soft/50 line-through" : "text-ink-soft"}`}>
        {label}
      </span>
    </label>
  );
}
